import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { formatFechaLocal } from '../utils/date.utils';
import {
  PieChart,
  Pie,
  Cell,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  AreaChart,
  Area,
} from 'recharts';
import {
  Users,
  UserCheck,
  Church,
  CalendarDays,
  TrendingUp,
  Calendar,
  Droplets,
  Heart,
  HandHeart,
  MapPin,
  ChevronRight,
} from 'lucide-react';
import { DashboardService, type DashboardStats } from '../services/dashboard/dashboard.service';
import { ProgramacionService, type ProgramacionSemanal } from '../services/programacion/programacion.service';
import { EstadoSeguimientoBadge } from '../components/programacion/EstadoSeguimientoBadge';
import { usePermissions } from '../hooks/usePermissions';

interface UserData {
  id: string;
  email: string;
  nombreCompleto: string;
  tipoUsuario: 'administrador' | 'miembro';
  estado: boolean;
  roles: Array<{
    id: string;
    nombre: string;
    descripcion?: string;
    modulos: Array<{
      id: string;
      nombre: string;
      descripcion?: string;
      ruta?: string;
      icono?: string;
      orden: number;
      permisos: Array<{
        id: string;
        nombre: string;
        descripcion?: string;
      }>;
    }>;
  }>;
}

const COLORS = {
  red: '#F87171',
  blue: '#60A5FA',
  green: '#34D399',
  purple: '#A78BFA',
  yellow: '#FBBF24',
  pink: '#F472B6',
  gray: '#9CA3AF',
  orange: '#FB923C',
  teal: '#2DD4BF',
  indigo: '#818CF8',
};

const CHURCH_COLORS = ['#F87171', '#60A5FA', '#FBBF24', '#A78BFA'];

// Wrapper que debouncea el resize para evitar re-renders masivos durante animaciones del sidebar
const DebouncedChartContainer = ({ children, height }: { children: React.ReactNode; height: number }) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const [dims, setDims] = useState({ width: 0, height });

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;

    let timeout: ReturnType<typeof setTimeout>;
    const ro = new ResizeObserver((entries) => {
      clearTimeout(timeout);
      timeout = setTimeout(() => {
        for (const entry of entries) {
          const { width, height } = entry.contentRect;
          setDims({ width, height });
        }
      }, 150);
    });

    ro.observe(el);
    return () => {
      ro.disconnect();
      clearTimeout(timeout);
    };
  }, [height]);

  return (
    <div ref={containerRef} className="w-full" style={{ height }}>
      {dims.width > 0 ? (
        <ResponsiveContainer width={dims.width} height={dims.height}>
          {children}
        </ResponsiveContainer>
      ) : null}
    </div>
  );
};

export const DashboardPage = () => {
  const navigate = useNavigate();
  const { hasPermission } = usePermissions();
  const canViewProgramacion = hasPermission('Programación Semanal', 'ver');
  const [programacionActual, setProgramacionActual] = useState<ProgramacionSemanal | null>(null);
  const [userData, setUserData] = useState<UserData | null>(null);
  const [stats, setStats] = useState<DashboardStats | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const token = localStorage.getItem('accessToken');
    const userDataStr = localStorage.getItem('userData');

    if (!token || !userDataStr) {
      navigate('/login');
      return;
    }

    try {
      const parsedUserData = JSON.parse(userDataStr);
      setUserData(parsedUserData);
    } catch (err) {
      console.error('Error parsing user data:', err);
      navigate('/login');
      return;
    }

    // Cargar estadísticas
    loadStats();
  }, [navigate]);

  const loadStats = async () => {
    try {
      setIsLoading(true);
      const data = await DashboardService.getStats();
      setStats(data);
      setError(null);
    } catch (err: any) {
      console.error('Error cargando estadísticas:', err);
      setError('No se pudieron cargar las estadísticas del dashboard');
    } finally {
      setIsLoading(false);
    }
  };



  useEffect(() => {
    if (!canViewProgramacion) return;
    let cancelled = false;
    ProgramacionService.getActual()
      .then((p) => { if (!cancelled) setProgramacionActual(p); })
      .catch(() => { if (!cancelled) setProgramacionActual(null); });
    return () => { cancelled = true; };
  }, [canViewProgramacion]);

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (!userData) {
    return null;
  }

  if (!stats || !stats.kpis) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-500">Cargando estadísticas...</p>
        </div>
      </div>
    );
  }


  // ─── KPI Card Component ───
  const KpiCard = ({
    title,
    value,
    subtitle,
    icon: Icon,
    color,
    onClick,
  }: {
    title: string;
    value: number;
    subtitle?: string;
    icon: any;
    color: string;
    onClick?: () => void;
  }) => (
    <div
      onClick={onClick}
      className={`bg-white rounded-xl shadow-sm border border-gray-100 p-5 min-w-0 overflow-hidden transition-all duration-200 hover:shadow-md ${
        onClick ? 'cursor-pointer' : ''
      }`}
    >
      <div className="flex items-start justify-between">
        <div className="flex-1">
          <p className="text-xs font-medium text-gray-500 mb-1">{title}</p>
          <p className="text-xs font-medium text-gray-900">{value.toLocaleString()}</p>
          {subtitle && <p className="text-xs text-gray-400 mt-1">{subtitle}</p>}
        </div>
        <div
          className="p-3 rounded-lg"
          style={{ backgroundColor: `${color}15` }}
        >
          <Icon className="w-6 h-6" style={{ color }} />
        </div>
      </div>
    </div>
  );

  // ─── Chart Card Component ───
  const ChartCard = ({
    title,
    children,
    className = '',
  }: {
    title: string;
    children: React.ReactNode;
    className?: string;
  }) => (
    <div className={`bg-white rounded-xl shadow-sm border border-gray-100 p-5 min-w-0 overflow-hidden ${className}`}>
      <h3 className="text-xs font-medium text-gray-800 mb-4">{title}</h3>
      {children}
    </div>
  );

  // ─── Custom Tooltip ───
  const CustomTooltip = ({ active, payload, label }: any) => {
    if (active && payload && payload.length) {
      return (
        <div className="bg-white p-3 rounded-lg shadow-lg border border-gray-100 text-xs">
          {label && <p className="font-medium text-gray-700 mb-1">{label}</p>}
          {payload.map((entry: any, index: number) => (
            <p key={index} className="text-gray-600">
              <span
                className="inline-block w-2 h-2 rounded-full mr-2"
                style={{ backgroundColor: entry.color }}
              />
              {entry.name}: {entry.value.toLocaleString()}
            </p>
          ))}
        </div>
      );
    }
    return null;
  };

  return (
    <div className="space-y-6">
      {/* Error banner */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-center gap-3">
          <div className="text-red-500">
            <TrendingUp className="w-5 h-5" />
          </div>
          <p className="text-red-700 text-xs">{error}</p>
          <button
            onClick={loadStats}
            className="ml-auto text-xs text-red-600 hover:text-red-800 font-medium"
          >
            Reintentar
          </button>
        </div>
      )}

      {/* KPIs */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-4 min-w-0">
        <KpiCard
          title="Total Usuarios"
          value={stats.kpis.totalUsuarios}
          icon={Users}
          color={COLORS.purple}
          onClick={() => navigate('/dashboard/miembros/usuarios')}
        />
        <KpiCard
          title="Total Miembros"
          value={stats.kpis.totalMiembros}
          icon={UserCheck}
          color={COLORS.blue}
          onClick={() => navigate('/dashboard/miembros/membresias')}
        />
        <KpiCard
          title="Ministerios"
          value={stats.kpis.totalMinisterios}
          icon={Church}
          color={COLORS.red}
          onClick={() => navigate('/dashboard/iglesia/ministerios')}
        />
        <KpiCard
          title="Eventos"
          value={stats.kpis.totalEventos}
          icon={CalendarDays}
          color={COLORS.green}
          onClick={() => navigate('/dashboard/eventos')}
        />
        <KpiCard
          title="Nuevos este mes"
          value={stats.kpis.miembrosNuevosEsteMes}
          subtitle="Miembros registrados"
          icon={TrendingUp}
          color={COLORS.orange}
        />
        <KpiCard
          title="Eventos este mes"
          value={stats.kpis.eventosEsteMes}
          subtitle="Próximos a realizarse"
          icon={Calendar}
          color={COLORS.teal}
        />
      </div>

      {/* Próximos Eventos */}
      {stats.eventos.proximosEventos.length > 0 && (
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-5">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-xs font-medium text-gray-800">Próximos Eventos</h3>
            <button
              onClick={() => navigate('/dashboard/eventos')}
              className="text-xs text-blue-600 hover:text-blue-800 font-medium flex items-center gap-1"
            >
              Ver todos <ChevronRight className="w-4 h-4" />
            </button>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-4">
            {stats.eventos.proximosEventos.map((evento) => (
              <div
                key={evento.id}
                className="border border-gray-100 rounded-lg p-4 hover:shadow-md transition-shadow cursor-pointer"
                onClick={() => navigate('/dashboard/eventos')}
              >
                <div className="flex items-start gap-3">
                  <div className="p-2 bg-blue-50 rounded-lg shrink-0">
                    <Calendar className="w-4 h-4 text-blue-600" />
                  </div>
                  <div className="min-w-0">
                    <p className="text-xs font-medium text-gray-900 truncate">{evento.titulo}</p>
                    <p className="text-xs text-gray-500 mt-1">
                      {formatFechaLocal(evento.fecha, {
                        day: 'numeric',
                        month: 'short',
                        year: 'numeric',
                      })}
                    </p>
                    {evento.lugar && (
                      <p className="text-xs text-gray-400 flex items-center gap-1 mt-1">
                        <MapPin className="w-3 h-3" /> {evento.lugar}
                      </p>
                    )}
                    <span
                      className={`inline-block mt-2 text-xs px-2 py-0.5 rounded-full ${
                        evento.estado === 'programado'
                          ? 'bg-blue-100 text-blue-700'
                          : evento.estado === 'en_curso'
                          ? 'bg-green-100 text-green-700'
                          : 'bg-gray-100 text-gray-700'
                      }`}
                    >
                      {evento.estado === 'en_curso' ? 'En curso' : evento.estado}
                    </span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Programación Semanal (semana en curso) */}
      {canViewProgramacion && programacionActual && (
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-5">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h3 className="text-xs font-medium text-gray-800">Programación Semanal</h3>
              <p className="text-[11px] text-gray-500 mt-0.5">
                {formatFechaLocal(programacionActual.fechaInicio, { day: 'numeric', month: 'short' })} –{' '}
                {formatFechaLocal(programacionActual.fechaFin, { day: 'numeric', month: 'short', year: 'numeric' })}
                {' · '}{programacionActual.descripcion}
              </p>
            </div>
            <button
              onClick={() => navigate('/dashboard/iglesia/programacion-semanal')}
              className="text-xs text-purple-600 hover:text-purple-800 font-medium flex items-center gap-1"
            >
              Ver todo <ChevronRight className="w-4 h-4" />
            </button>
          </div>
          {(programacionActual.asignaciones ?? []).length === 0 ? (
            <p className="text-xs text-gray-400 py-4 text-center">
              Esta semana no tiene asignaciones registradas.
            </p>
          ) : (
            <>
              <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">
                {(programacionActual.asignaciones ?? []).slice(0, 6).map((a) => (
                  <div
                    key={a.id}
                    className="border border-gray-100 rounded-lg p-3 hover:shadow-md transition-shadow"
                  >
                    <div className="flex items-start gap-3">
                      <div className="p-2 bg-purple-50 rounded-lg shrink-0">
                        <CalendarDays className="w-4 h-4 text-purple-600" />
                      </div>
                      <div className="min-w-0 flex-1">
                        <p className="text-xs font-medium text-gray-900 truncate">
                          {a.participante?.nombreCompleto ?? 'Sin participante'}
                        </p>
                        <p className="text-[11px] text-gray-500 mt-0.5 capitalize">
                          {formatFechaLocal(a.fecha, { weekday: 'long', day: 'numeric', month: 'short' })} · {a.horaInicio} – {a.horaFin}
                        </p>
                        <div className="flex flex-wrap gap-1.5 mt-2">
                          <EstadoSeguimientoBadge estado={a.estado} />
                          {a.ministerio && (
                            <span className="text-[10px] px-2 py-0.5 rounded-full bg-purple-50 text-purple-700">
                              {a.ministerio.nombre}
                            </span>
                          )}
                          {(a.actividad || a.rol) && (
                            <span className="text-[10px] px-2 py-0.5 rounded-full bg-gray-100 text-gray-600">
                              {a.actividad ?? a.rol}
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
              {(programacionActual.asignaciones?.length ?? 0) > 6 && (
                <p className="text-[11px] text-gray-400 mt-3 text-center">
                  +{(programacionActual.asignaciones?.length ?? 0) - 6} asignación(es) más esta semana
                </p>
              )}
            </>
          )}
        </div>
      )}

      {/* Charts Row 1 */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 min-w-0">
        {/* Género */}
        <ChartCard title="Miembros por Género">
          <DebouncedChartContainer height={250}>
            <PieChart>
              <Pie
                data={stats.miembros.porGenero}
                cx="50%"
                cy="50%"
                innerRadius={60}
                outerRadius={90}
                paddingAngle={4}
                dataKey="valor"
                nameKey="nombre"
                label={({ name, percent }: any) =>
                  `${name}: ${((percent || 0) * 100).toFixed(0)}%`
                }
              >
                {stats.miembros.porGenero.map((entry, index) => (
                  <Cell key={index} fill={entry.color || CHURCH_COLORS[index]} />
                ))}
              </Pie>
              <Tooltip content={<CustomTooltip />} />
              <Legend />
            </PieChart>
            </DebouncedChartContainer>
          <div className="flex justify-center gap-6 mt-2">
            {stats.miembros.porGenero.map((g) => (
              <div key={g.nombre} className="text-center">
                <p className="text-xs font-medium" style={{ color: g.color }}>
                  {g.valor}
                </p>
                <p className="text-xs text-gray-500">{g.nombre}</p>
              </div>
            ))}
          </div>
        </ChartCard>

        {/* Rangos de Edad */}
        <ChartCard title="Distribución por Edad">
          <DebouncedChartContainer height={280}>
            <BarChart data={stats.miembros.porRangoEdad} layout="vertical">
              <CartesianGrid strokeDasharray="3 3" horizontal={false} stroke="#E5E7EB" />
              <XAxis type="number" tick={{ fontSize: 12 }} />
              <YAxis
                dataKey="nombre"
                type="category"
                width={140}
                tick={{ fontSize: 11 }}
              />
              <Tooltip content={<CustomTooltip />} />
              <Bar
                dataKey="valor"
                name="Miembros"
                fill={COLORS.blue}
                radius={[0, 4, 4, 0]}
              />
            </BarChart>
            </DebouncedChartContainer>
        </ChartCard>

        {/* Usuarios por Tipo */}
        <ChartCard title="Usuarios del Sistema">
          <DebouncedChartContainer height={250}>
            <PieChart>
              <Pie
                data={stats.usuarios.porTipo}
                cx="50%"
                cy="50%"
                outerRadius={90}
                paddingAngle={4}
                dataKey="valor"
                nameKey="nombre"
                label={({ name, value, percent }: any) =>
                  `${name}: ${((percent || 0) * 100).toFixed(0)}%`
                }
              >
                {stats.usuarios.porTipo.map((entry, index) => (
                  <Cell key={index} fill={entry.color || CHURCH_COLORS[index]} />
                ))}
              </Pie>
              <Tooltip content={<CustomTooltip />} />
              <Legend />
            </PieChart>
            </DebouncedChartContainer>
          <div className="flex justify-center gap-6 mt-2">
            {stats.usuarios.porTipo.map((u) => (
              <div key={u.nombre} className="text-center">
                <p className="text-xs font-medium" style={{ color: u.color }}>
                  {u.valor}
                </p>
                <p className="text-xs text-gray-500">{u.nombre}</p>
              </div>
            ))}
          </div>
        </ChartCard>
      </div>

      {/* Charts Row 2 */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 min-w-0">
        {/* Registros por mes */}
        <ChartCard title="Nuevos Miembros por Mes">
          <DebouncedChartContainer height={280}>
            <AreaChart data={stats.miembros.registrosPorMes}>
              <defs>
                <linearGradient id="colorMiembros" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor={COLORS.blue} stopOpacity={0.3} />
                  <stop offset="95%" stopColor={COLORS.blue} stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
              <XAxis dataKey="mes" tick={{ fontSize: 12 }} />
              <YAxis tick={{ fontSize: 12 }} allowDecimals={false} />
              <Tooltip content={<CustomTooltip />} />
              <Area
                type="monotone"
                dataKey="cantidad"
                name="Miembros"
                stroke={COLORS.blue}
                fillOpacity={1}
                fill="url(#colorMiembros)"
                strokeWidth={2}
              />
            </AreaChart>
            </DebouncedChartContainer>
        </ChartCard>

        {/* Eventos por mes */}
        <ChartCard title="Eventos Creados por Mes">
          <DebouncedChartContainer height={280}>
            <AreaChart data={stats.eventos.eventosPorMes}>
              <defs>
                <linearGradient id="colorEventos" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor={COLORS.green} stopOpacity={0.3} />
                  <stop offset="95%" stopColor={COLORS.green} stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
              <XAxis dataKey="mes" tick={{ fontSize: 12 }} />
              <YAxis tick={{ fontSize: 12 }} allowDecimals={false} />
              <Tooltip content={<CustomTooltip />} />
              <Area
                type="monotone"
                dataKey="cantidad"
                name="Eventos"
                stroke={COLORS.green}
                fillOpacity={1}
                fill="url(#colorEventos)"
                strokeWidth={2}
              />
            </AreaChart>
            </DebouncedChartContainer>
        </ChartCard>
      </div>

      {/* Charts Row 3 */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 min-w-0">
        {/* Estado Civil */}
        <ChartCard title="Estado Civil">
          <DebouncedChartContainer height={250}>
            <BarChart data={stats.miembros.porEstadoCivil}>
              <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
              <XAxis dataKey="nombre" tick={{ fontSize: 11 }} angle={-20} textAnchor="end" height={60} />
              <YAxis tick={{ fontSize: 12 }} allowDecimals={false} />
              <Tooltip content={<CustomTooltip />} />
              <Bar dataKey="valor" name="Miembros" fill={COLORS.purple} radius={[4, 4, 0, 0]} />
            </BarChart>
            </DebouncedChartContainer>
        </ChartCard>

        {/* Top Zonas */}
        <ChartCard title="Top Zonas / Barrios">
          <DebouncedChartContainer height={250}>
            <BarChart data={stats.miembros.topZonas} layout="vertical">
              <CartesianGrid strokeDasharray="3 3" horizontal={false} stroke="#E5E7EB" />
              <XAxis type="number" tick={{ fontSize: 12 }} />
              <YAxis
                dataKey="zona"
                type="category"
                width={120}
                tick={{ fontSize: 11 }}
              />
              <Tooltip content={<CustomTooltip />} />
              <Bar dataKey="cantidad" name="Miembros" fill={COLORS.orange} radius={[0, 4, 4, 0]} />
            </BarChart>
            </DebouncedChartContainer>
        </ChartCard>

        {/* Eventos por Estado */}
        <ChartCard title="Eventos por Estado">
          <DebouncedChartContainer height={250}>
            <PieChart>
              <Pie
                data={stats.eventos.porEstado}
                cx="50%"
                cy="50%"
                innerRadius={50}
                outerRadius={80}
                paddingAngle={4}
                dataKey="valor"
                nameKey="nombre"
                label={({ percent }: any) =>
                  `${((percent || 0) * 100).toFixed(0)}%`
                }
              >
                {stats.eventos.porEstado.map((entry, index) => (
                  <Cell key={index} fill={entry.color || CHURCH_COLORS[index]} />
                ))}
              </Pie>
              <Tooltip content={<CustomTooltip />} />
              <Legend />
            </PieChart>
            </DebouncedChartContainer>
        </ChartCard>
      </div>

      {/* Row 4: Ministerios y Espiritual */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 min-w-0">
        {/* Ministerios Stats */}
        <ChartCard title="Ministerios">
          <div className="grid grid-cols-2 gap-4 mb-4">
            <div className="bg-green-50 rounded-lg p-4 text-center">
              <p className="text-xs font-medium text-green-600">{stats.ministerios.activos}</p>
              <p className="text-xs text-green-700">Activos</p>
            </div>
            <div className="bg-gray-50 rounded-lg p-4 text-center">
              <p className="text-xs font-medium text-gray-600">{stats.ministerios.inactivos}</p>
              <p className="text-xs text-gray-700">Inactivos</p>
            </div>
            <div className="bg-blue-50 rounded-lg p-4 text-center">
              <p className="text-xs font-medium text-blue-600">{stats.ministerios.conLider}</p>
              <p className="text-xs text-blue-700">Con Líder</p>
            </div>
            <div className="bg-red-50 rounded-lg p-4 text-center">
              <p className="text-xs font-medium text-red-600">{stats.ministerios.sinLider}</p>
              <p className="text-xs text-red-700">Sin Líder</p>
            </div>
          </div>
          <h4 className="text-xs font-medium text-gray-600 mb-2">Top Ministerios</h4>
          <div className="space-y-2">
            {stats.ministerios.topMinisterios.map((m, i) => (
              <div
                key={i}
                className="flex items-center justify-between bg-gray-50 rounded-lg px-3 py-2"
              >
                <span className="text-xs text-gray-700 truncate flex-1">{m.nombre}</span>
                <span className="text-xs font-medium bg-blue-100 text-blue-700 px-2 py-0.5 rounded-full ml-2">
                  {m.integrantes} integrantes
                </span>
              </div>
            ))}
          </div>
        </ChartCard>

        {/* Estadísticas Espirituales */}
        <ChartCard title="Estadísticas Espirituales">
          <div className="grid grid-cols-2 gap-4">
            <div className="bg-blue-50 rounded-lg p-4 flex items-center gap-3">
              <div className="p-2 bg-blue-100 rounded-lg">
                <Droplets className="w-5 h-5 text-blue-600" />
              </div>
              <div>
                <p className="text-xs font-medium text-blue-700">{stats.espiritual.bautizados}</p>
                <p className="text-xs text-blue-600">Bautizados</p>
              </div>
            </div>
            <div className="bg-gray-50 rounded-lg p-4 flex items-center gap-3">
              <div className="p-2 bg-gray-200 rounded-lg">
                <Droplets className="w-5 h-5 text-gray-500" />
              </div>
              <div>
                <p className="text-xs font-medium text-gray-700">{stats.espiritual.noBautizados}</p>
                <p className="text-xs text-gray-600">No Bautizados</p>
              </div>
            </div>
            <div className="bg-purple-50 rounded-lg p-4 flex items-center gap-3">
              <div className="p-2 bg-purple-100 rounded-lg">
                <Heart className="w-5 h-5 text-purple-600" />
              </div>
              <div>
                <p className="text-xs font-medium text-purple-700">{stats.espiritual.oracionFe}</p>
                <p className="text-xs text-purple-600">Oración de Fe</p>
              </div>
            </div>
            <div className="bg-green-50 rounded-lg p-4 flex items-center gap-3">
              <div className="p-2 bg-green-100 rounded-lg">
                <HandHeart className="w-5 h-5 text-green-600" />
              </div>
              <div>
                <p className="text-xs font-medium text-green-700">
                  {stats.espiritual.pertenecenMinisterio}
                </p>
                <p className="text-xs text-green-600">En Ministerio</p>
              </div>
            </div>
          </div>
        </ChartCard>
      </div>

      {/* Registros por Ministerio */}
      <div className="grid grid-cols-1 gap-6 min-w-0">
        <ChartCard title="Miembros Registrados por Ministerio">
          {(!stats.ministerios.registrosPorMinisterio || stats.ministerios.registrosPorMinisterio.length === 0) ? (
            <div className="text-center text-gray-500 py-8 text-xs">
              No hay registros de miembros por ministerio
            </div>
          ) : (
            <DebouncedChartContainer height={320}>
              <BarChart
                data={stats.ministerios.registrosPorMinisterio || []}
                layout="vertical"
                margin={{ top: 5, right: 30, left: 20, bottom: 5 }}
              >
                <CartesianGrid strokeDasharray="3 3" horizontal={false} stroke="#E5E7EB" />
                <XAxis type="number" tick={{ fontSize: 12 }} allowDecimals={false} />
                <YAxis
                  dataKey="nombre"
                  type="category"
                  width={150}
                  tick={{ fontSize: 11 }}
                />
                <Tooltip content={<CustomTooltip />} />
                <Bar
                  dataKey="registrados"
                  name="Miembros registrados"
                  fill={COLORS.purple}
                  radius={[0, 4, 4, 0]}
                  label={{ position: 'right', fill: '#6B7280', fontSize: 12 }}
                />
              </BarChart>
            </DebouncedChartContainer>
          )}
        </ChartCard>
      </div>




    </div>
  );
};

export default DashboardPage;
